import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Genome } from 'src/providers/genome/genome';
import { Logger } from 'src/providers/logger';
import { PhotoNotes } from "src/providers/photo-notes/photo-notes";
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { ToDosQueriesService } from 'src/shared/services/to-dos-queries.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Loading } from 'src/shared/components/loading/loading';
import { PhotoNoteUrlModel } from 'src/shared/components/photo-caption/photo-caption.component';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';

import { toDoAutocomplete } from 'src/shared/constants/toDoAutocomplete';
import { Util } from 'src/shared/util/util';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

@Component({
    templateUrl: 'add-to-do.html',
    providers: [PhotoCaptionService],
    standalone: false
})

export class ToDosAddToDoPage implements OnInit, OnDestroy {

  addressId: any;
  customFieldTypeItems = [];
  customFieldForm: CustomFieldFormModel[] = [];
  estimatedTimes: TidySelectNumberValueModel[];
  errorMessage: string;
  form: UntypedFormGroup;
  isRightSideContent: boolean;
  important = false;
  itemCategoryItems: any;
  isAddingCustomFields: boolean;
  loaded = false;
  preSelectedRoom: number;
  photoUrls: PhotoNoteUrlModel[] = [];
  roomError: boolean;
  rooms: TidySelectNumberValueModel[];
  selectedListId: any;
  selectedGroups = [];
  submitted: boolean;
  toDoAutoComplete = toDoAutocomplete;
  taskCategoryOptions: any;
  itemsAutoComplete = [];
  itemNameSubscription: Subscription;
  hasInventoryField: boolean;
  boundAddToDo = this.addToDo.bind(this);
  categoryItems: any;
  toDo: any;
  pageTitle = 'Add Checklist Item';
  buttonText = 'Add Checklist Item';

  private readonly toDosQueries = inject(ToDosQueriesService);

constructor(
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private logger: Logger,
    private photoNotes: PhotoNotes,
    private photoCaptionService: PhotoCaptionService,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private toDos: ToDos,
    public util: Util,
    private modalCtrl: ModalController
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      note: [''],
      time: [5, Validators.required],
      roomObject: [''],
      itemCategory: [''],
      itemYearInstalled: [''],
      itemModel: [''],
      itemNewModel: [''],
      itemCharacteristics: [''],
      itemInventoryMinQuantity: [''],
      itemInventoryCurrentQuantity: [''],
      itemCurrentQuantity: [''],
      itemMinQuantity: [''],
      itemName: [null]
    });
  }

  @Loading('', true)
  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.rooms = this.navCtrl.getParam('rooms') || dialogParams.rooms;
    this.addressId = this.navCtrl.getParam('addressId') || dialogParams.addressId;
    this.selectedListId = this.navCtrl.getParam('selectedListId') || dialogParams.selectedListId;
    this.toDo = this.navCtrl.getParam('toDo') || dialogParams.toDo;
    this.pageTitle = this.toDo ? 'Edit Checklist Item' : 'Add Checklist Item';
    this.buttonText = this.toDo ? 'Save Checklist Item' : 'Add Checklist Item';
    this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    this.buildEstimatedTimes();
    await this.buildCustomFieldTypeItems();
    await this.getItemCategoryItems();
    await this.buildCategoryItems();
    if (this.toDo) {
      await this.patchFormWithToDo();
    }
    this.populateRoomCheckboxes();
    this.setupItemNameSearch();
    this.loaded = true;
  }

  async patchFormWithToDo() {
    this.form.patchValue({
      title: this.toDo.title,
      time: this.toDo.estimated_time || 5,
      note: this.toDo.note || ''
    });
    this.important = this.toDo.is_important || false;
    if (this.toDo.address_room_id) {
      this.selectedGroups.push(this.toDo.address_room_id);
    }
    if (this.toDo.address_room_ids && Array.isArray(this.toDo.address_room_ids)) {
      for (const roomId of this.toDo.address_room_ids) {
        if (!this.selectedGroups.includes(roomId)) {
          this.selectedGroups.push(roomId);
        }
      }
    }
    if (this.toDo.room_object) {
      this.hasInventoryField = true;
      this.form.patchValue({
        itemName: this.toDo.room_object.name
      });
      if (this.toDo.room_object) {
        const categoryId = this.toDo.room_object.genome_category_id;
        this.form.patchValue({
          itemCategory: categoryId
        });
      }
      this.form.get('itemName').setValidators(Validators.required);
      this.form.get('itemCategory').setValidators(Validators.required);
      this.form.get('itemName').updateValueAndValidity();
      this.form.get('itemCategory').updateValueAndValidity();
    }
    if (this.toDo.options && this.toDo.options.length > 0) {
      this.isAddingCustomFields = true;
      const customOptions = this.toDo.options.filter(opt => opt.name !== 'Take Inventory');
      for (const option of customOptions) {
        const taskCategoryOption = this.taskCategoryOptions.find(tco => tco.name === option.name);
        if (!taskCategoryOption) {
          continue;
        }
        const formGroup = this.fb.group({
          type: [taskCategoryOption.id, Validators.required],
          label: [option.notes || '', Validators.required],
          required: [option.required !== false, Validators.required],
          options: this.fb.array([])
        });
        const customField = {
          form: formGroup,
          options: [],
          objects: [],
          numberOfRequiredOptions: 0,
          maxLength: null
        };
        if (option.name === 'Checkbox(es)') {
          customField.numberOfRequiredOptions = 1;
        } else if (option.name === 'Select') {
          customField.numberOfRequiredOptions = 2;
        } else if (option.name === 'Text Area') {
          customField.maxLength = 40;
        }
        if (option.allowed_values && option.allowed_values.length > 0) {
          for (const allowedValue of option.allowed_values) {
            const optionFormGroup = this.fb.group({
              option: [allowedValue, Validators.required]
            });
            customField.options.push(optionFormGroup);
          }
        }
        this.customFieldForm.push(customField);
      }
    }
  }

  ngOnDestroy() {
    if (this.itemNameSubscription) {
      this.itemNameSubscription.unsubscribe();
    }
  }

  async buildCategoryItems() {
    const categories = await this.genome.getCategories();
    this.categoryItems = categories
      .filter(category => category.consumable === true)
      .map((category) => ({
        value: category.id,
        viewValue: category.name,
        icon: category.default_icon_url,
        category
      }));
  }

  setupItemNameSearch() {
    this.itemNameSubscription = this.form.get('itemName')?.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(value => this.searchItems(value));
  }

  async searchItems(searchTerm: string) {
    if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.length < 2) {
      this.itemsAutoComplete = [];
      return;
    }
    try {
      const params: any = {
        page: 1,
        per_page: 100,
        consumable: true,
        search: searchTerm.toLowerCase()
      };
      const roomObjectsResp = await this.genome.getInventoryRoomObjects(params);
      const roomObjects = roomObjectsResp.body;
      this.itemsAutoComplete = roomObjects.reduce((uniqueItems, item) => {
        if (!uniqueItems.some(uniqueItem => uniqueItem.value === item.name)) {
          uniqueItems.push({
            value: item.name,
            display: item.name,
            id: item.model.category.id
          });
        }
        return uniqueItems;
      }, []);
    } catch (err) {
      this.logger.error('Error searching items', err);
    }
  }

  selectItem(item: any) {
    if (item && item.id) {
      this.form.patchValue({ itemName: item.display || item.value, itemCategory: item.id });
    }
  }

  async getItemCategoryItems() {
    const itemCategories = await this.genome.getCategories();
    this.itemCategoryItems = itemCategories.map((category) => ({
        value: category.id,
        viewValue: category.name
      }));
  }

  populateRoomCheckboxes() {
    const preSelectedRoom = localStorage.getItem('roomId');
    if (preSelectedRoom) {
      this.preSelectedRoom = parseFloat(preSelectedRoom);
      this.selectRoom(this.preSelectedRoom);
    }
  }

  buildEstimatedTimes()  {
    const times = [];
    for (let i = 1; i < 32; i++) {
      times.push({
        viewValue: `${i  } minutes`,
        value: i
      });
    }
    this.estimatedTimes = times;
  }

  async buildCustomFieldTypeItems() {
    this.taskCategoryOptions = await this.toDos.getTaskCategoryOptions();
    this.taskCategoryOptions.forEach((item) => {
      if (item.name !== 'Vacuum' && item.name !== 'Mop' && item.name !== 'Take Inventory') {
        this.customFieldTypeItems.push({
          viewValue: item.name,
          value: item.id
        });
      }
    });
  }

  selectRoom(id) {
    this.roomError = false;
    this.submitted = false;
    let alreadySelected = false;
    this.selectedGroups.find((room) => {
      if (id === room) {
        alreadySelected = true;
      };
    });
    if (!alreadySelected) {
      this.selectedGroups.push(id);
    } else {
      this.selectedGroups = this.selectedGroups.filter((group) => id !== group);
    }
  }

  async addToDo() {
    try {
      this.roomError = false;
      this.submitted = true;
      if (this.selectedGroups.length === 0) {
        this.roomError = true;
      }
      let hasFieldOptionError = false;
      this.customFieldForm.forEach((field) => {
        const hasNotSelectedFieldTypeYet = !field.hasOwnProperty('numberOfRequiredOptions');
        if (field.options.length < field.numberOfRequiredOptions || hasNotSelectedFieldTypeYet) {
          hasFieldOptionError = true;
        }
        field.options.forEach((option) => {
          option['hasCommaError'] = false;
          if (option.controls.option.value.includes(',')) {
            option['hasCommaError'] = true;
            hasFieldOptionError = true;
          }
        });
      });
      if (!this.form.valid || hasFieldOptionError || this.roomError) {
        return;
      }
      const payload = {
        title:                  this.form.value.title,
        note:                   this.form.value.note,
        estimated_time:         this.form.value.time,
        is_important:           this.important,
        task_category_options:  [],
      }
      if (!this.toDo) {
        payload['address_task_group_ids'] = this.selectedGroups;
        payload['address_id'] = this.addressId;
        payload['address_task_list_id'] = this.selectedListId;
        payload['task_type'] = 'cleaning';
      }
      this.customFieldForm.forEach((field) => {
        const item = {
          id: field.form.value.type,
          notes: field.form.value.label,
          required: field.form.value.required
        }
        if (field.options.length > 0) {
          item['allowed_values'] = '';
          field.options.forEach((option) => {
            item['allowed_values'] += option.controls.option.value;
            item['allowed_values'] += ', ';
          });
        }
        if (item['allowed_values']) {
          item['allowed_values'] = item['allowed_values'].substring(0, item['allowed_values'].length - 2);
        }
        payload['task_category_options'].push(item);
      });
      if (this.hasInventoryField) {
        payload['room_object'] = {
          name: this.form.value.itemName,
          genome_category_id: this.form.value.itemCategory,
          inventory_settings: {
            strategies: [{
              type: 'task',
              inventory_type: 'count_remaining',
              address_task_lists: [{
                address_task_list_id: this.selectedListId,
                address_task_group_ids: this.selectedGroups,
              }],
            }],
          },
        };
        /*const item = {
          id: this.taskCategoryOptions.find((option) => option.name === 'Take Inventory').id,
          notes: `Count # of ${this.form.value.itemName}`,
          required: true
        }
        payload['task_category_options'].push(item);*/
      }
      const tasks = this.toDo ? [await this.toDos.updateToDo(this.toDo.id, payload)] : await this.toDos.addToDo(this.addressId, this.selectedListId, payload);
      await this.assignPhotoNotes(tasks);
      // Invalidate list detail cache after adding/updating task
      this.toDosQueries.invalidateListDetail(this.selectedListId);
      const route = localStorage.getItem('roomId') ? 'card' : 'to-dos';
      const dialogParams = await this.storage.retrieve('dialog-params');
      if (dialogParams?.room) {dialogParams.room = null;}
      await this.storage.save('dialog-params', dialogParams);
      this.rightSidePanelService.navigateTo(route);
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }


  async takePhotos() {
    try {
      const newPhotoNotes = await this.photoCaptionService.getMultipleImages('task');
      this.photoUrls = [...this.photoUrls, ...newPhotoNotes];
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  async assignPhotoNotes(tasks) {
    await Promise.all(tasks.map(async (task, index) => {
      if (this.selectedGroups[index]) {
        await this.uploadPhotos(this.selectedGroups[index], task.id, 'group');
      }
    }));
  }

  async uploadPhotos(roomId: string, taskId: number, roomType) {
    const photoNote = roomType === 'group' ?
      this.toDos.addGroupTaskPhotoNote.bind(this.toDos, roomId, taskId) :
      this.toDos.taskPhotoNotes.bind(this.toDos, roomId, taskId);
    await this.photoCaptionService.uploadPhotosGeneric(
      this.photoUrls,
      'task',
      photoNote,
      this.photoNotes.photoCaption.bind(this.photoNotes),
      roomType
    );
  }

  expandFields() {
    this.addCustomField();
    this.isAddingCustomFields = true;
  }

  addCustomField() {
    const formGroup = this.fb.group({
      type: ['', Validators.required],
      label: ['', Validators.required],
      required: [true, Validators.required],
      options: this.fb.array([])
    });
    this.customFieldForm.push({
      form: formGroup,
      options: [],
      objects: []
    });
  }

  removeField(fieldIndex) {
    this.customFieldForm.splice(fieldIndex, 1);
    if (this.customFieldForm.length === 0) {
      this.isAddingCustomFields = false;
    }
  }

  selectFieldType(taskCategoryOptionId, i) {
    this.submitted = false;
    const selectedOption = this.taskCategoryOptions.find((option) => option.id === taskCategoryOptionId);
    this.customFieldForm[i]['maxLength'] = null;
    this.customFieldForm[i]['numberOfRequiredOptions'] = 0;
    if (selectedOption.name === 'Checkbox(es)') {
      this.customFieldForm[i]['numberOfRequiredOptions'] = 1;
    }
    if (selectedOption.name === 'Select') {
      this.customFieldForm[i]['numberOfRequiredOptions'] = 2;
    }
    if (selectedOption.name === 'Text Area') {
      this.customFieldForm[i]['maxLength'] = 40;
      this.clearLabelIfTooLong(i);
    }
  }

  clearLabelIfTooLong(i) {
    if (this.customFieldForm[i].form.value.label?.length > 40) {
      this.customFieldForm[i].form.patchValue({label: ''});
    }
  }

  addOption(index) {
    this.submitted = false;
    const formGroup = this.fb.group({
      option: ['', Validators.required]
    });
    this.customFieldForm[index].options.push(formGroup);
  }

  removeOption(customFieldFormIndex, optionIndex) {
    this.customFieldForm[customFieldFormIndex].options.splice(optionIndex, 1);
  }

  selectRequired(form, selection) {
    form.form.patchValue({required: !selection});
  }

  addInventoryField() {
    this.hasInventoryField = true;
    this.form.get('itemName').setValidators(Validators.required);
    this.form.get('itemCategory').setValidators(Validators.required);
    this.form.get('itemName').updateValueAndValidity();
    this.form.get('itemCategory').updateValueAndValidity();
  }

  removeInventoryField() {
    this.hasInventoryField = false;
    this.form.get('itemName').clearValidators();
    this.form.get('itemCategory').clearValidators();
    this.form.get('itemName').updateValueAndValidity();
    this.form.get('itemCategory').updateValueAndValidity();
  }

  async deleteToDo() {
    const params: ConfirmPageParamsModel = {
      title: 'Delete To-Do?',
      body: 'You will not be able to undo this action.',
      backText: 'Go Back',
      confirmText: 'Delete To-Do',
      confirmAction: this.confirmDeleteToDo.bind(this)
    }
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmDeleteToDo() {
    try {
      await this.toDos.deleteToDo(this.toDo.id);
      // Invalidate list detail cache after deleting task
      this.toDosQueries.invalidateListDetail(this.selectedListId);
      const dialogParams = await this.storage.retrieve('dialog-params');
      if (dialogParams?.room) {dialogParams.room = null;}
      await this.storage.save('dialog-params', dialogParams);
      this.rightSidePanelService.navigateTo('card');
      this.modalCtrl.dismiss();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

}

interface CustomFieldFormModel {
  form: UntypedFormGroup;
  options: any;
  objects: any;
  optionsError?: boolean;
  numberOfRequiredOptions?: number;
}
